/**
 * Single source of truth for password requirements across the app.
 *
 * The platform enforces: minimum 12 characters, and at least one each of
 * uppercase, lowercase, number, and special character. Every surface that
 * lets a user set a new password (invite, reset, change) should validate
 * against these rules and render the requirements via <PasswordRequirements />.
 */
export interface PasswordRule {
  id: string
  label: string
  test: (password: string) => boolean
}

export const PASSWORD_RULES: PasswordRule[] = [
  { id: 'length', label: 'At least 12 characters', test: (p) => p.length >= 12 },
  { id: 'uppercase', label: 'At least one uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'At least one lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'number', label: 'At least one number', test: (p) => /[0-9]/.test(p) },
  { id: 'special', label: 'At least one special character (!@#$%^&*())', test: (p) => /[!@#$%^&*()]/.test(p) },
]

/** Generic message for on-submit errors; the live checklist shows the detail. */
export const PASSWORD_REQUIREMENTS_MESSAGE = 'Password does not meet all requirements'

/** Returns the rules the given password fails (empty array means fully valid). */
export function getFailedPasswordRules(password: string): PasswordRule[] {
  return PASSWORD_RULES.filter((rule) => !rule.test(password))
}

/** True when the password satisfies every rule. */
export function isPasswordValid(password: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(password))
}
